// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Post-login onboarding  (/welcome/profile → terms → /welcome/delivery → consent → aboard)
// Step 4 of 5 — Delivery location. A thin page around components/delivery/AddressForm (the map,
// search, GPS and address fields live there, shared with the address book). This page adds the
// onboarding header, "Skip for now", and the rules: reachable only right after a login, and a
// customer who already has a saved address is passed straight on.
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import PageTransition from "../components/PageTransition";
import AddressForm from "../components/delivery/AddressForm";
import useAuth from "../hooks/useAuth";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { fetchAddresses } from "../utils/addresses";
import { hasConsented } from "../utils/consent";

export default function DeliveryLocation() {
  useDocumentTitle("Delivery location");
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const arrivedFromLogin = (location.state as { fromLogin?: boolean } | null)?.fromLogin === true;
  const signedIn = auth.status === "signed-in";
  const [checking, setChecking] = useState(true);

  const onward = useCallback(
    () => navigate(hasConsented() ? "/welcome/aboard" : "/welcome/consent", { state: { fromLogin: true }, replace: true }),
    [navigate],
  );

  // A customer who already saved an address isn't asked again.
  useEffect(() => {
    if (!signedIn) return;
    let cancelled = false;
    fetchAddresses().then(
      (saved) => {
        if (cancelled) return;
        if (saved.length > 0) onward();
        else setChecking(false);
      },
      () => !cancelled && setChecking(false),
    );
    return () => {
      cancelled = true;
    };
  }, [signedIn, onward]);

  if (!arrivedFromLogin || auth.status === "signed-out") return <Navigate to="/" replace />;

  return (
    <PageTransition>
      <div className="min-h-screen bg-foam-50 pt-8 text-ocean-950 dark:bg-ocean-950 dark:text-foam-50">
        <header className="sticky top-8 z-40 bg-foam-50/90 shadow-[0_1px_8px_rgba(0,0,0,0.04)] backdrop-blur-xl dark:bg-ocean-950/90">
          <div className="mx-auto flex h-16 max-w-xl items-center justify-between gap-2 px-4">
            <div className="flex items-center gap-2">
              <button
                type="button"
                aria-label="Go back"
                onClick={() => navigate("/welcome/terms", { state: { fromLogin: true } })}
                className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-ocean-700/10 focus-visible:outline-2 focus-visible:outline-teal-600"
              >
                <ArrowLeft className="h-6 w-6" aria-hidden="true" />
              </button>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-teal-700 dark:text-teal-300">Step 4 of 5</p>
                <h1 className="font-display text-lg font-semibold leading-tight">Delivery location</h1>
              </div>
            </div>
            <button
              type="button"
              onClick={onward}
              className="flex h-11 items-center rounded-lg px-2 text-sm font-semibold text-ocean-800 hover:text-teal-700 dark:text-foam-100"
            >
              Skip for now
            </button>
          </div>
        </header>

        {checking ? (
          <p role="status" className="mt-24 flex items-center justify-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
            Checking your saved addresses…
          </p>
        ) : (
          <AddressForm initial={null} submitLabel="Confirm address & continue" onSaved={onward} />
        )}
      </div>
    </PageTransition>
  );
}
